import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { apiService } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';

const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda","Argentina","Armenia","Australia","Austria",
  "Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bhutan",
  "Bolivia","Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso","Burundi","Cabo Verde","Cambodia",
  "Cameroon","Canada","Central African Republic","Chad","Chile","China","Colombia","Comoros","Congo (Brazzaville)","Congo (Kinshasa)",
  "Costa Rica","Croatia","Cuba","Cyprus","Czech Republic","Denmark","Djibouti","Dominica","Dominican Republic","Ecuador",
  "Egypt","El Salvador","Equatorial Guinea","Eritrea","Estonia","Eswatini","Ethiopia","Fiji","Finland","France",
  "Gabon","Gambia","Georgia","Germany","Ghana","Greece","Grenada","Guatemala","Guinea","Guinea-Bissau",
  "Guyana","Haiti","Honduras","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland",
  "Israel","Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kiribati","Kuwait","Kyrgyzstan",
  "Laos","Latvia","Lebanon","Lesotho","Liberia","Libya","Liechtenstein","Lithuania","Luxembourg","Madagascar",
  "Malawi","Malaysia","Maldives","Mali","Malta","Marshall Islands","Mauritania","Mauritius","Mexico","Micronesia",
  "Moldova","Monaco","Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia","Nauru","Nepal",
  "Netherlands","New Zealand","Nicaragua","Niger","Nigeria","North Korea","North Macedonia","Norway","Oman","Pakistan",
  "Palau","Palestine","Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland","Portugal","Qatar",
  "Romania","Russia","Rwanda","Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines","Samoa","San Marino","Sao Tome and Principe","Saudi Arabia",
  "Senegal","Serbia","Seychelles","Sierra Leone","Singapore","Slovakia","Slovenia","Solomon Islands","Somalia","South Africa",
  "South Korea","South Sudan","Spain","Sri Lanka","Sudan","Suriname","Sweden","Switzerland","Syria","Taiwan",
  "Tajikistan","Tanzania","Thailand","Timor-Leste","Togo","Tonga","Trinidad and Tobago","Tunisia","Turkey","Turkmenistan",
  "Tuvalu","Uganda","Ukraine","United Arab Emirates","United Kingdom","United States","Uruguay","Uzbekistan","Vanuatu","Vatican City",
  "Venezuela","Vietnam","Yemen","Zambia","Zimbabwe"
];

const PAYMENT_METHODS = [
  { id: 'MOBILE_MONEY', label: 'Mobile Money', icon: 'Smartphone', description: 'MTN, Vodafone, AirtelTigo' },
  { id: 'CARD', label: 'Card Payments', icon: 'CreditCard', description: 'Visa, Mastercard, Amex' },
  { id: 'CRYPTO', label: 'Cryptocurrency', icon: 'Bitcoin', description: 'BTC, ETH, USDT' },
];

const DURATION_OPTIONS = [
  { value: 'ONE_HOUR', label: '1 Hour' },
  { value: 'ONE_DAY', label: '24 Hours' },
  { value: 'SEVEN_DAYS', label: '7 Days' },
  { value: 'THIRTY_DAYS', label: '30 Days' },
  { value: 'CUSTOM', label: 'Custom Date & Time' },
];

const CreatePaymentLinkModal = ({ onClose, onCreated }) => {
  const { businessCode } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [generatedId, setGeneratedId] = useState('');
  const [apiError, setApiError] = useState('');

  const [countrySearch, setCountrySearch] = useState('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const countryRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (countryRef?.current && !countryRef?.current?.contains(e?.target)) {
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCountries = COUNTRIES?.filter(c =>
    c?.toLowerCase()?.includes(countrySearch?.toLowerCase())
  );

  const [form, setForm] = useState({
    title: '',
    amount: '',
    currency: 'KES',
    description: '',
    allMethods: true,
    selectedMethods: { MOBILE_MONEY: true, CARD: true, CRYPTO: true },
    expirationType: 'ONE_DAY',
    customExpiry: '',
    billingFirstName: '',
    billingLastName: '',
    billingEmail: '',
    billingPhone: '',
    billingAddress: '',
    billingCity: '',
    billingCountry: '',
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const toggleMethod = (id) => {
    setForm(prev => {
      const updated = { ...prev?.selectedMethods, [id]: !prev?.selectedMethods?.[id] };
      return { ...prev, selectedMethods: updated };
    });
  };

  const validate = () => {
    const e = {};
    if (!form?.title?.trim()) e.title = 'Title is required';
    if (!form?.amount || isNaN(parseFloat(form?.amount)) || parseFloat(form?.amount) <= 0)
      e.amount = 'Enter a valid amount';
    if (!form?.allMethods && !Object.values(form?.selectedMethods)?.some(Boolean))
      e.methods = 'Select at least one payment method';
    if (form?.expirationType === 'CUSTOM' && !form?.customExpiry)
      e.customExpiry = 'Select expiry date and time';
    return e;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = validate();
    if (Object.keys(errs)?.length > 0) { setErrors(errs); return; }
    setLoading(true);
    setApiError('');

    const paymentMethods = form?.allMethods
      ? ['MOBILE_MONEY', 'CARD', 'CRYPTO']
      : Object.keys(form?.selectedMethods)?.filter(k => form?.selectedMethods?.[k]);

    const requestPayload = {
      business_code: businessCode,
      title: form?.title,
      currency: form?.currency,
      amount: parseFloat(form?.amount),
      payment_methods: paymentMethods,
      expiration: form?.expirationType,
    };

    if (form?.description?.trim()) {
      requestPayload.description = form?.description;
    }

    if (form?.expirationType === 'CUSTOM' && form?.customExpiry) {
      requestPayload.custom_expiration = new Date(form?.customExpiry)?.toISOString();
    }

    const hasBillingData = form?.billingFirstName || form?.billingLastName || form?.billingEmail ||
      form?.billingPhone || form?.billingAddress || form?.billingCity || form?.billingCountry;

    if (hasBillingData) {
      requestPayload.address = {
        first_name: form?.billingFirstName || undefined,
        last_name: form?.billingLastName || undefined,
        email: form?.billingEmail || undefined,
        phone: form?.billingPhone || undefined,
        address: form?.billingAddress || undefined,
        city: form?.billingCity || undefined,
        country: form?.billingCountry || undefined,
      };
    }

    try {
      const response = await apiService?.createPaymentLink(requestPayload);
      const data = response?.data;
      const detail = data?.details?.[0] || {};

      const linkCode = detail?.link_code || '';
      const linkId = detail?.id || '';

      setGeneratedId(linkId);
      setGeneratedLink(linkCode);
      setStep(2);

      if (onCreated) {
        onCreated({
          id: linkId,
          link: linkCode,
          form,
          detail,
        });
      }
    } catch (err) {
      const errMsg = err?.response?.data?.status_description
        || err?.response?.data?.message
        || err?.response?.data?.detail
        || 'Failed to create payment link. Please try again.';
      setApiError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard?.writeText(generatedLink)?.then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleShare = (channel) => {
    const text = `Pay securely using this link: ${generatedLink}`;
    if (channel === 'email') {
      window.open(`mailto:?subject=Payment Link&body=${encodeURIComponent(text)}`);
    } else if (channel === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`);
    } else if (channel === 'sms') {
      window.open(`sms:?body=${encodeURIComponent(text)}`);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-surface rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-border">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Link" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-text-primary">
                {step === 1 ? 'Create Payment Link' : 'Payment Link Created'}
              </h2>
              <p className="text-xs text-text-secondary">
                {step === 1 ? 'Configure and generate a secure payment link' : 'Share your payment link with customers'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted text-text-secondary transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5">
          {step === 1 ? (
            <form id="create-link-form" onSubmit={handleSubmit} className="space-y-6">
              {/* API Error */}
              {apiError && (
                <div className="flex items-start space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
                  <Icon name="AlertCircle" size={15} className="text-error flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-error">{apiError}</p>
                </div>
              )}

              {/* Basic Info */}
              <div>
                <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center space-x-2">
                  <span className="w-5 h-5 bg-primary text-white rounded-full text-xs flex items-center justify-center font-bold">1</span>
                  <span>Payment Details</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-text-secondary mb-1">Link Title <span className="text-error">*</span></label>
                    <input
                      type="text"
                      value={form?.title}
                      onChange={e => handleChange('title', e?.target?.value)}
                      placeholder="e.g. Invoice #1234 Payment"
                      className={`w-full px-3 py-2 text-sm border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 ${errors?.title ? 'border-error' : 'border-border'}`}
                    />
                    {errors?.title && <p className="text-xs text-error mt-1">{errors?.title}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Amount <span className="text-error">*</span></label>
                    <div className="flex">
                      <select
                        value={form?.currency}
                        onChange={e => handleChange('currency', e?.target?.value)}
                        className="px-3 py-2 text-sm border border-r-0 border-border rounded-l-lg bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                      >
                        <option>KES</option>
                        <option>USD</option>
                        <option>EUR</option>
                        <option>GBP</option>
                      </select>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={form?.amount}
                        onChange={e => handleChange('amount', e?.target?.value)}
                        placeholder="0.00"
                        className={`flex-1 px-3 py-2 text-sm border rounded-r-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 ${errors?.amount ? 'border-error' : 'border-border'}`}
                      />
                    </div>
                    {errors?.amount && <p className="text-xs text-error mt-1">{errors?.amount}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Description (optional)</label>
                    <input
                      type="text"
                      value={form?.description}
                      onChange={e => handleChange('description', e?.target?.value)}
                      placeholder="Brief description for customer"
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Methods */}
              <div>
                <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center space-x-2">
                  <span className="w-5 h-5 bg-primary text-white rounded-full text-xs flex items-center justify-center font-bold">2</span>
                  <span>Payment Methods <span className="text-error">*</span></span>
                </h3>
                <div className="flex items-center space-x-3 mb-3">
                  <button
                    type="button"
                    onClick={() => handleChange('allMethods', true)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${form?.allMethods ? 'bg-primary text-white border-primary' : 'bg-background text-text-secondary border-border hover:border-primary/50'}`}
                  >
                    All Methods
                  </button>
                  <button
                    type="button"
                    onClick={() => handleChange('allMethods', false)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${!form?.allMethods ? 'bg-primary text-white border-primary' : 'bg-background text-text-secondary border-border hover:border-primary/50'}`}
                  >
                    Select Methods
                  </button>
                </div>
                {errors?.methods && <p className="text-xs text-error mb-2">{errors?.methods}</p>}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {PAYMENT_METHODS?.map(m => {
                    const isEnabled = form?.allMethods || form?.selectedMethods?.[m?.id];
                    return (
                      <button
                        key={m?.id}
                        type="button"
                        onClick={() => !form?.allMethods && toggleMethod(m?.id)}
                        disabled={form?.allMethods}
                        className={`flex items-center space-x-3 p-3 rounded-xl border text-left transition-all ${
                          isEnabled
                            ? 'border-primary bg-primary/5 text-text-primary' : 'border-border bg-background text-text-secondary opacity-60'
                        } ${form?.allMethods ? 'cursor-default' : 'cursor-pointer hover:border-primary/60'}`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isEnabled ? 'bg-primary/10' : 'bg-muted'}`}>
                          <Icon name={m?.icon} size={16} className={isEnabled ? 'text-primary' : 'text-text-secondary'} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold truncate">{m?.label}</p>
                          <p className="text-[10px] text-text-secondary truncate">{m?.description}</p>
                        </div>
                        {!form?.allMethods && (
                          <div className={`ml-auto w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${form?.selectedMethods?.[m?.id] ? 'bg-primary border-primary' : 'border-border'}`}>
                            {form?.selectedMethods?.[m?.id] && <Icon name="Check" size={10} color="white" />}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Expiration */}
              <div>
                <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center space-x-2">
                  <span className="w-5 h-5 bg-primary text-white rounded-full text-xs flex items-center justify-center font-bold">3</span>
                  <span>Expiration <span className="text-error">*</span></span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
                  {DURATION_OPTIONS?.map(opt => (
                    <button
                      key={opt?.value}
                      type="button"
                      onClick={() => handleChange('expirationType', opt?.value)}
                      className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${form?.expirationType === opt?.value ? 'bg-primary text-white border-primary' : 'bg-background text-text-secondary border-border hover:border-primary/50'}`}
                    >
                      {opt?.label}
                    </button>
                  ))}
                </div>
                {form?.expirationType === 'CUSTOM' && (
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Expiry Date & Time <span className="text-error">*</span></label>
                    <input
                      type="datetime-local"
                      value={form?.customExpiry}
                      onChange={e => handleChange('customExpiry', e?.target?.value)}
                      min={new Date()?.toISOString()?.slice(0, 16)}
                      className={`w-full sm:w-64 px-3 py-2 text-sm border rounded-lg bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 ${errors?.customExpiry ? 'border-error' : 'border-border'}`}
                    />
                    {errors?.customExpiry && <p className="text-xs text-error mt-1">{errors?.customExpiry}</p>}
                  </div>
                )}
              </div>

              {/* Billing Address (optional) */}
              <div>
                <h3 className="text-sm font-semibold text-text-primary mb-1 flex items-center space-x-2">
                  <span className="w-5 h-5 bg-primary text-white rounded-full text-xs flex items-center justify-center font-bold">4</span>
                  <span>Billing Address <span className="text-text-secondary font-normal text-xs">(optional)</span></span>
                </h3>
                <p className="text-xs text-text-secondary mb-3 ml-7">All billing address fields are optional.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">First Name</label>
                    <input type="text" value={form?.billingFirstName} onChange={e => handleChange('billingFirstName', e?.target?.value)} placeholder="First name" className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Last Name</label>
                    <input type="text" value={form?.billingLastName} onChange={e => handleChange('billingLastName', e?.target?.value)} placeholder="Last name" className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Email</label>
                    <input type="email" value={form?.billingEmail} onChange={e => handleChange('billingEmail', e?.target?.value)} placeholder="customer@email.com" className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Phone</label>
                    <input type="tel" value={form?.billingPhone} onChange={e => handleChange('billingPhone', e?.target?.value)} placeholder="254700111222" className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">City</label>
                    <input type="text" value={form?.billingCity} onChange={e => handleChange('billingCity', e?.target?.value)} placeholder="City" className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-text-secondary mb-1">Street Address</label>
                    <input type="text" value={form?.billingAddress} onChange={e => handleChange('billingAddress', e?.target?.value)} placeholder="Street address" className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div ref={countryRef} className="relative">
                    <label className="block text-xs font-medium text-text-secondary mb-1">Country</label>
                    <div
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary cursor-pointer flex items-center justify-between focus-within:ring-2 focus-within:ring-primary/30"
                      onClick={() => setCountryDropdownOpen(prev => !prev)}
                    >
                      <span className={form?.billingCountry ? 'text-text-primary' : 'text-text-secondary'}>
                        {form?.billingCountry || 'Select country'}
                      </span>
                      <Icon name={countryDropdownOpen ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-text-secondary ml-2 flex-shrink-0" />
                    </div>
                    {countryDropdownOpen && (
                      <div className="absolute z-50 mt-1 w-full bg-surface border border-border rounded-lg shadow-lg">
                        <div className="p-2 border-b border-border">
                          <input
                            type="text"
                            value={countrySearch}
                            onChange={e => setCountrySearch(e?.target?.value)}
                            placeholder="Search country..."
                            className="w-full px-2 py-1.5 text-sm border border-border rounded bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30"
                            autoFocus
                            onClick={e => e?.stopPropagation()}
                          />
                        </div>
                        <ul className="max-h-48 overflow-y-auto">
                          {filteredCountries?.length > 0 ? filteredCountries?.map(country => (
                            <li
                              key={country}
                              className={`px-3 py-2 text-sm cursor-pointer hover:bg-primary/10 ${form?.billingCountry === country ? 'bg-primary/10 text-primary font-medium' : 'text-text-primary'}`}
                              onClick={() => {
                                handleChange('billingCountry', country);
                                setCountryDropdownOpen(false);
                                setCountrySearch('');
                              }}
                            >
                              {country}
                            </li>
                          )) : (
                            <li className="px-3 py-2 text-sm text-text-secondary">No countries found</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </form>
          ) : (
            /* Success Step */
            <div className="flex flex-col items-center text-center py-4 space-y-6">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center">
                <Icon name="CheckCircle" size={32} className="text-success" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-primary">Payment Link Ready!</h3>
                <p className="text-sm text-text-secondary mt-1">Your secure payment link has been generated. Share it with your customer.</p>
              </div>
              {/* Link display */}
              <div className="w-full bg-muted/50 border border-border rounded-xl p-4">
                <p className="text-xs text-text-secondary mb-2 font-medium">Payment Link</p>
                <div className="flex items-center space-x-2">
                  <input
                    readOnly
                    value={generatedLink}
                    className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary focus:outline-none select-all"
                  />
                  <button
                    onClick={handleCopy}
                    className={`flex items-center space-x-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-shrink-0 ${copied ? 'bg-success text-white' : 'bg-primary text-white hover:bg-primary/90'}`}
                  >
                    <Icon name={copied ? 'Check' : 'Copy'} size={14} />
                    <span>{copied ? 'Copied!' : 'Copy'}</span>
                  </button>
                </div>
                <p className="text-xs text-text-secondary mt-2">Link ID: <span className="font-mono font-medium text-text-primary">{generatedId}</span></p>
              </div>
              {/* Share options */}
              <div className="w-full">
                <p className="text-xs font-medium text-text-secondary mb-3">Share via</p>
                <div className="flex items-center justify-center space-x-3">
                  <button onClick={() => handleShare('email')} className="flex flex-col items-center space-y-1.5 px-5 py-3 border border-border rounded-xl hover:bg-muted transition-colors group">
                    <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center group-hover:bg-blue-100">
                      <Icon name="Mail" size={16} className="text-blue-600" />
                    </div>
                    <span className="text-xs text-text-secondary">Email</span>
                  </button>
                  <button onClick={() => handleShare('whatsapp')} className="flex flex-col items-center space-y-1.5 px-5 py-3 border border-border rounded-xl hover:bg-muted transition-colors group">
                    <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center group-hover:bg-green-100">
                      <Icon name="MessageCircle" size={16} className="text-green-600" />
                    </div>
                    <span className="text-xs text-text-secondary">WhatsApp</span>
                  </button>
                  <button onClick={() => handleShare('sms')} className="flex flex-col items-center space-y-1.5 px-5 py-3 border border-border rounded-xl hover:bg-muted transition-colors group">
                    <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center group-hover:bg-purple-100">
                      <Icon name="MessageSquare" size={16} className="text-purple-600" />
                    </div>
                    <span className="text-xs text-text-secondary">SMS</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-border flex-shrink-0">
          {step === 1 ? (
            <>
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-text-secondary border border-border rounded-lg hover:bg-muted transition-colors">
                Cancel
              </button>
              <button
                type="submit"
                form="create-link-form"
                disabled={loading}
                className="flex items-center space-x-2 px-5 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                {loading ? <Icon name="Loader" size={15} className="animate-spin" /> : <Icon name="Link" size={15} />}
                <span>{loading ? 'Generating...' : 'Generate Link'}</span>
              </button>
            </>
          ) : (
            <button onClick={onClose} className="px-5 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatePaymentLinkModal;
